import React, { useEffect, useState, useRef, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import tempIcon from "../../../assets/icons/temp.webp";
import waterIcon from "../../../assets/icons/windDrop.webp";
import sunIcon from "../../../assets/icons/sun.webp";
import phIcon from "../../../assets/icons/ph.webp";
import ModalStructure from "../../../components/ModalStructure";
import {
  deleteMeasurementById,
  updateLabRecord,
} from "../../../services/panel";
import { IS_LOCAL_ENV, socket } from "../../../App";
import Notification from "../../../components/ui-elements/Notification";
import { isOutOfRange } from "../../../utils/other";

const translateTurn = {
  EarlyMorning: "Madrugada",
  Morning: "Mañana",
  Afternoon: "Tarde",
  Night: "Noche",
};

const defaultProperties = {
  temperature: {
    title: "Temperatura",
    icon: tempIcon,
    details:
      "Su unidad de medida es Celsius, el ingreso de datos es numérico y dos decimales.",
    startColor: "#fc786e",
    endColor: "#fff5f4",
    lineColor: "#fc786e",
    unit: "°C",
  },
  humidity: {
    title: "Humedad",
    icon: waterIcon,
    details: "Es %, el ingreso de datos es numérico y dos decimales.",
    startColor: "#76e2f8",
    endColor: "#feffff",
    lineColor: "#76e2f8",
    unit: "%",
  },
  light: {
    title: "Luz",
    icon: sunIcon,
    details:
      "Su unidad de medida es lm, el ingreso de datos es numérico y dos decimales.",
    startColor: "#dbb300",
    endColor: "#fffefa",
    lineColor: "#dbb300",
    unit: "lm",
  },
  ph: {
    title: "Ph",
    icon: phIcon,
    details:
      "Su unidad de medida es pH, el ingreso de datos es numérico. (0 - 7 es ácido/ 7.1 - 14 es alcalino)",
    startColor: "#7bb25f",
    endColor: "#f3f8f0",
    lineColor: "#7bb25f",
    unit: "",
  },
};

const Chart = ({ propInfo }) => {
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showDeleteMeasurement, setShowDeleteMeasurement] = useState(false);

  // Memoizamos las opciones del gráfico
  const chartOptions = useMemo(() => {
    const values = [];
    const labels = [];
    let previousDate = "";
    propInfo.data.map((reg) => {
      const currentDate = new Date(reg.dateAndTime).toLocaleDateString(
        "es-ES",
        {
          day: "2-digit",
          month: "2-digit",
        }
      );

      // Agregar valor y configuración de color según wasLate
      values.push({
        ambient: reg.ambient,
        value: reg.value,
        id: reg.id,
        wasLate: reg.wasLate || false,
        wasUpdated: reg.createdAt != reg.updatedAt,
        updatedAt: reg.updatedAt,
        unit: defaultProperties[reg.property].unit,
        dateAndTime: reg.dateAndTime,
        turn: translateTurn[reg.turn],
        property: reg.property,
        observations: reg.observations,
        itemStyle: {
          borderColor: isOutOfRange(reg.property, reg.value)
            ? "#ff0034"
            : reg.wasLate
            ? "#ff6c00"
            : "#707878", // Borde anaranjado o azul según wasLate
          borderWidth: 2, // Grosor del borde
          color: "#ffffff", // Relleno blanco (transparente)
        },
      });

      if (currentDate !== previousDate) {
        labels.push(currentDate);
        previousDate = currentDate;
      } else {
        labels.push(""); // Para mantener la estructura del eje X
      }
    });

    const isMobile = window.matchMedia("(max-width: 767px)").matches; // Detecta si es móvil (puedes ajustar este valor según tu diseño)

    return {
      tooltip: {
        trigger: "item",
        show: !isMobile, // El tooltip se mostrará solo si no es móvil
        formatter: function (params) {
          return `${params.value} ${defaultProperties[propInfo.property].unit}`;
        },
      },
      xAxis: {
        type: "category",
        data: labels,
        axisLabel: {
          fontFamily: "Poppins",
          fontSize: 12,
          margin: 20,
          rotate: 67.5,
          fontWeight: "bold",
          color: "#000000",
        },
        boundaryGap: false,
      },
      yAxis: {
        type: "value",
        axisLabel: {
          fontFamily: "Poppins",
          fontSize: 12,
          margin: 10,
          formatter: (value) => {
            if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
            if (value >= 1e3) return `${(value / 1e3).toFixed(1)}k`;
            return value;
          },
        },
      },
      series: [
        {
          data: values,
          type: "line",
          smooth: false,
          symbol: "circle", // Asegurarse de que los puntos sean círculos
          symbolSize: 10, // Tamaño de los puntos
          lineStyle: {
            color: defaultProperties[propInfo.property].lineColor,
          },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: defaultProperties[propInfo.property].startColor,
                },
                {
                  offset: 1,
                  color: defaultProperties[propInfo.property].endColor,
                },
              ],
            },
          },
        },
      ],
      devicePixelRatio: window.devicePixelRatio || 2,
    };
  }, [propInfo]);

  // Actualización manual del gráfico en caso necesario
  useEffect(() => {
    setLoading(true);
    if (chartRef.current) {
      const echartsInstance = chartRef.current.getEchartsInstance();
      echartsInstance.setOption(chartOptions, true); // true para el merge de opciones
    }
    setLoading(false);
  }, [chartOptions]);

  const handlePointClick = (data) => {
    setSelectedPoint(data);
    //console.log("Punto clickeado:", data);
    // Realiza la acción específica
  };

  useEffect(() => {
    if (chartRef.current) {
      const echartsInstance = chartRef.current.getEchartsInstance();

      // Agregar manejador de clics
      echartsInstance.on("click", (params) => {
        if (handlePointClick && params.componentType === "series") {
          handlePointClick(params.data); // Pasar información del punto clickeado
        }
      });

      // Limpieza del evento en desmontaje
      return () => {
        echartsInstance.off("click");
      };
    }
  }, [chartOptions, handlePointClick]);

  const hadleUpdatePoint = (pointId, newValue) => {
    const updatePoint = async (pointId, newValue) => {
      try {
        setUpdating(true);
        const response = await updateLabRecord(pointId, newValue); //al enviar por socket, se actualiza por si solo
        socket.emit("createdLabMeasurement", {
          data: response.doc,
          isUpdatedRecord: true,
        });
        setUpdating(false);
        Notification({
          title: "Operación completada",
          text: `Se actualizó el registro`,
          type: "Success",
        });
      } catch (err) {
        setUpdating(false);
        IS_LOCAL_ENV && console.error(err);
        Notification({
          title: "Error",
          text: `Algo salió mal...`,
          type: "Error",
        });
      }
    };
    updatePoint(pointId, newValue);
  };

  const removeMeasurement = (id) => {
    const deleteMeasurement = async (id) => {
      try {
        const res = await deleteMeasurementById(id);
        //eliminar localmente
        setUpdating(true);
        socket.emit("deletedLabMeasurement", {
          data: selectedPoint,
        });
        Notification({
          title: "Operación completada",
          text: `El registro fue eliminado exitosamente`,
          type: "Success",
        });
        setUpdating(false);
      } catch (err) {
        IS_LOCAL_ENV && console.error(err);
        Notification({
          title: "Error",
          text: `Algo salió mal...`,
          type: "Error",
        });
      }
    };
    deleteMeasurement(id);
  };

  return (
    <>
      <div className="flex flex-col max-w-[520px] md:p-4 pt-4 pb-0 border-[1px] bg-white m-2 md:m-4 border-accent-green shadow-md rounded-[10px]">
        <div className=" flex flex-row px-6 pt-2 gap-x-6 w-full h-[130px]">
          <div className="flex items-start justify-center ">
            <img
              src={defaultProperties[propInfo.property].icon}
              className="max-w-[170px] w-full h-auto"
            />
          </div>
          <div className="ml-[20px] flex flex-col gap-y-2">
            <h2 className="text-black font-bold font-poppins text-lg">
              {defaultProperties[propInfo.property].title}
            </h2>
            <span className="text-default-gray font-poppins text-sm">
              {defaultProperties[propInfo.property].details}
            </span>
          </div>
        </div>

        <div className="max-h-[500px] max-w-[500px] w-full">
          <ReactECharts
            ref={chartRef}
            option={chartOptions} // Usamos las opciones memoizadas
            //className="h-[450px] relative"
            showLoading={loading}
            renderer="svg"
            notMerge={false}
            lazyUpdate={false}
            onPointClick={handlePointClick}
          />
          {selectedPoint != null && (
            <ModalStructure
              handleCloseModal={() => {
                setSelectedPoint(null);
              }}
              customMaxW="max-w-[400px]"
            >
              <div className="flex flex-col font-poppins items-start justify-center gap-y-2 my-2 ">
                <h3 className=" font-bold text-dark-green text-wrap text-lg w-full text-center">
                  Detalle de registro
                </h3>
                <span className=" text-default-gray text-wrap text-base gap-x-2">
                  <span className="font-bold">Turno:</span>
                  &nbsp;
                  {selectedPoint.turn}
                </span>
                <span className=" text-default-gray text-wrap text-base gap-x-2">
                  <span className="font-bold">Fecha y hora:</span>
                  &nbsp;
                  <span className="text-wrap">
                    {new Date(selectedPoint.dateAndTime).toLocaleString(
                      "es-PE"
                    )}
                  </span>
                </span>
                {selectedPoint.wasUpdated && (
                  <span className=" text-blue-500 text-wrap text-base gap-x-2">
                    <span className="font-bold">Actualizado el:</span>
                    &nbsp;
                    {new Date(selectedPoint.updatedAt).toLocaleString("es-PE")}
                  </span>
                )}
                <div className="flex flex-row items-center  gap-x-2 text-default-gray">
                  <span className="font-bold">Valor:</span>
                  <input
                    type="number"
                    value={`${selectedPoint.value}`}
                    onChange={(e) =>
                      setSelectedPoint((prev) => ({
                        ...prev,
                        value: parseFloat(e.target.value),
                      }))
                    }
                    className="w-[100px] rounded-full py-1 px-2 text-dark-green outline-none bg-transparent"
                  />
                  <span>{selectedPoint.unit}</span>
                </div>
                <span className=" text-default-gray flex flex-row text-wrap text-base gap-x-2">
                  <span className="font-bold">Observaciones:</span>
                  &nbsp;
                  {selectedPoint.observations ? (
                    <p className="text-wrap max-w-[200px]">
                      {selectedPoint.observations}
                    </p>
                  ) : (
                    <span className="text-default-gray">
                      No hay observaciones
                    </span>
                  )}
                </span>
                {selectedPoint.wasLate && (
                  <span className=" text-orange-500 text-sm">
                    * Registro tardío
                  </span>
                )}
                {isOutOfRange(selectedPoint.property, selectedPoint.value) && (
                  <span className=" text-red-500 text-sm">
                    * Valor fuera del rango aceptable
                  </span>
                )}

                <div className="flex w-full flex-row gap-x-2 pt-2 justify-center">
                  <button
                    className={` w-fit bg-dark-green text-xl  rounded-full py-2 px-4 text-white ${
                      updating
                        ? "bg-default-gray cursor-not-allowed"
                        : "bg-dark-green hover:bg-accent-green"
                    } transition-all duration-100 font-bold font-poppins`}
                    type="submit"
                    onClick={() => {
                      hadleUpdatePoint(selectedPoint.id, selectedPoint.value);
                    }}
                    disabled={updating}
                  >
                    <span className="text-nowrap">Actualizar</span>
                  </button>
                  <button
                    className={` w-fit  text-xl  rounded-full py-2 px-4 text-white ${
                      updating
                        ? "bg-default-gray cursor-not-allowed"
                        : "bg-red-500 hover:bg-red-300"
                    } transition-all duration-100 font-bold font-poppins`}
                    onClick={() => {
                      setShowDeleteMeasurement(true);
                    }}
                  >
                    <span
                      className={` w-fit  text-xl  rounded-full py-2 px-4 text-white ${
                        updating
                          ? "bg-default-gray cursor-not-allowed"
                          : "bg-red-500 hover:bg-red-300"
                      } transition-all duration-100 font-bold font-poppins`}
                    >
                      Eliminar
                    </span>
                  </button>
                </div>
              </div>
            </ModalStructure>
          )}
          {showDeleteMeasurement && (
            <ModalStructure
              handleCloseModal={() => setShowDeleteMeasurement(false)}
              customMaxW={"max-w-[300px]"}
            >
              <div className="flex flex-col gap-y-4 font-poppins ">
                <div className="flex w-full justify-center">
                  <h3 className="modalTitle">Eliminar medición</h3>
                </div>
                <div className="modal-body">
                  <p>¿Está seguro de que desea eliminar la medición?</p>
                </div>
                <div className="flex flex-row w-full justify-center gap-x-2">
                  <button
                    className={` w-fit bg-dark-green text-xl  rounded-full py-2 px-4 text-white bg-dark-green hover:bg-accent-green transition-all duration-100 font-bold font-poppins`}
                    onClick={() => setShowDeleteMeasurement(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    className={` w-fit  text-xl  rounded-full py-2 px-4 text-white ${
                      updating
                        ? "bg-default-gray cursor-not-allowed"
                        : "bg-red-500 hover:bg-red-300"
                    } transition-all duration-100 font-bold font-poppins`}
                    onClick={() => {
                      removeMeasurement(selectedPoint.id);
                      setShowDeleteMeasurement(false);
                    }}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </ModalStructure>
          )}
        </div>
      </div>
    </>
  );
};

export default Chart;
